import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrgContext from '../context/OrgContext';

const UsersManager = () => {
    const { activeOrg, user } = useContext(OrgContext);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState({
        name: '',
        email: '',
        googleID: '',
        bank_account: '',
        phone: '',
        hrate: '',
        picture: '',
        roleID: '',
        organizationIDs: [],
        locationIDs: []
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;
    const token = sessionStorage.getItem('token');

    const availableOrganizations = user?.organizationIDs || [];
    const availableLocations = user?.locationIDs || [];

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/users`, {
                headers: { Authorization: `Bearer ${token}`, orgid: `${activeOrg ? activeOrg._id : ''}` }
            });
            setUsers(response.data);
            setFilteredUsers(response.data.slice(0, usersPerPage));
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Error fetching users');
        }
    }, [token, activeOrg, usersPerPage]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoles(response.data.roles);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Error fetching roles');
        }
    }, [token]);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [fetchUsers, fetchRoles]);

    useEffect(() => {
        const filtered = users.filter(user =>
            user.name.toLowerCase().includes(search.toLowerCase()) ||
            user.email.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredUsers(filtered.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage));
    }, [search, users, currentPage]);

    const openDialog = (user) => {
        if (user) {
            setSelectedUser({
                ...user,
                roleID: user.roleID?._id || user.roleID || '',  // Ensure roleID is correctly assigned
                organizationIDs: user.organizationIDs.map(org => org._id),
                locationIDs: user.locationIDs.map(loc => loc._id)
            });
        } else {
            setSelectedUser({
                name: '',
                email: '',
                googleID: undefined,
                bank_account: '',
                phone: '',
                hrate: '',
                picture: '',
                roleID: '',
                organizationIDs: [],
                locationIDs: []
            });
        }
        setDialogOpen(true);
    };


    const closeDialog = () => setDialogOpen(false);

    const handleCreateOrUpdate = async () => {
        try {
            const userPayload = {
                name: selectedUser.name,
                email: selectedUser.email,
                bank_account: selectedUser.bank_account,
                phone: selectedUser.phone,
                hrate: selectedUser.hrate,
                picture: selectedUser.picture,
                roleID: selectedUser.roleID,
                organizationIDs: selectedUser.organizationIDs,
                locationIDs: selectedUser.locationIDs
            };

            if (selectedUser._id) {
                await axios.put(`${process.env.REACT_APP_SRV}/users/${selectedUser._id}`, userPayload, {
                    headers: { Authorization: `Bearer ${token}`, orgid: `${activeOrg ? activeOrg._id : ''}` }
                });
                toast.success('User updated successfully');
            } else {
                await axios.post(`${process.env.REACT_APP_SRV}/users`, userPayload, {
                    headers: { Authorization: `Bearer ${token}`, orgid: `${activeOrg ? activeOrg._id : ''}` }
                });
                toast.success('User created successfully');
            }

            fetchUsers();
            closeDialog();
        } catch (error) {
            toast.error('Error creating/updating user');
        }
    };

    // Function to handle deletion of a user
    const handleDelete = async (userID) => {
        try {
            await axios.delete(`${process.env.REACT_APP_SRV}/users/${userID}`, {
                headers: { Authorization: `Bearer ${token}`, orgid: `${activeOrg ? activeOrg._id : ''}` }
            });
            toast.success('User deleted successfully');
            fetchUsers(); // Refresh the user list after deletion
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Error deleting user');
        }
    };


    const handleCheckboxChange = (id, type) => {
        setSelectedUser((prevState) => {
            const newSelection = prevState[type].includes(id)
                ? prevState[type].filter(item => item !== id)
                : [...prevState[type], id];
            return { ...prevState, [type]: newSelection };
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const startIndex = (page - 1) * usersPerPage;
        setFilteredUsers(users.slice(startIndex, startIndex + usersPerPage));
    };

    return (
        <div>
            <ToastContainer position="bottom-right" />
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search Users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="px-4 py-2 bg-gray-800 rounded text-white"
                />
                <button
                    onClick={() => openDialog(null)}
                    className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
                >
                    Create User
                </button>
            </div>
            <ul>
                {filteredUsers.map((user) => (
                    <li key={user._id} className="bg-gray-800 p-4 mb-2 rounded flex justify-between items-center">
                        <div>
                            <h3 className="text-xl">{user?.name || 'Unnamed User'}</h3>
                            <p className="text-gray-400">Email: {user?.email || 'No email provided'}</p>
                        </div>
                        <div className="flex">
                            <button
                                onClick={() => openDialog(user)}
                                className="bg-blue-600 px-2 py-1 rounded hover:bg-blue-700 mr-2"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(user._id)}
                                className="bg-red-600 px-2 py-1 rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="flex justify-center">
                {Array.from({ length: Math.ceil(users.length / usersPerPage) }, (_, index) => (
                    <button
                        key={index}
                        onClick={() => handlePageChange(index + 1)}
                        className={`px-3 py-1 mx-1 rounded ${currentPage === index + 1 ? 'bg-blue-600' : 'bg-gray-600'} hover:bg-blue-700`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
            {dialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg w-full max-w-3xl">
                        <h3 className="text-xl mb-4">{selectedUser._id ? 'Edit User' : 'Add New User'}</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <input
                                type="text"
                                placeholder="User Name"
                                value={selectedUser.name || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            />
                            <select
                                value={selectedUser.roleID || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, roleID: e.target.value })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            >
                                <option value="">Select Role</option>
                                {roles.map(role => (
                                    <option key={role._id} value={role._id}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="email"
                                placeholder="User Email"
                                value={selectedUser.email || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            />
                            <input
                                type="text"
                                placeholder="Phone"
                                value={selectedUser.phone || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            />
                            <input
                                type="text"
                                placeholder="Bank Account"
                                value={selectedUser.bank_account || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, bank_account: e.target.value })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            />
                            <input
                                type="number"
                                placeholder="Hourly Rate"
                                value={selectedUser.hrate || ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, hrate: parseFloat(e.target.value) })}
                                className="block w-full p-2 bg-gray-700 text-white rounded mb-2"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                                <h4 className="text-lg mb-2">Assign Organizations</h4>
                                {availableOrganizations.map(org => (
                                    <div key={org._id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedUser.organizationIDs.includes(org._id)}
                                            onChange={() => handleCheckboxChange(org._id, 'organizationIDs')}
                                            className="mr-2"
                                        />
                                        <label className="text-white">{org.orgName}</label>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <h4 className="text-lg mb-2">Assign Locations</h4>
                                {availableLocations.map(location => (
                                    <div key={location._id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedUser.locationIDs.includes(location._id)}
                                            onChange={() => handleCheckboxChange(location._id, 'locationIDs')}
                                            className="mr-2"
                                        />
                                        <label className="text-white">{location.name}</label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end mt-4">
                            <button
                                onClick={closeDialog}
                                className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateOrUpdate}
                                className="bg-green-600 px-4 py-2 ml-2 rounded hover:bg-green-700"
                            >
                                {selectedUser._id ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersManager;
